import {
  skipToken,
  useIsFetching,
  useIsMutating,
  useQuery,
  useQueryClient,
  useMutation,
  UseMutationOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  DangerousEquipment,
  DangerousEquipmentInfo,
  DemandManagementStatusInventoryCenterConfig,
  DemandManagementStatusInventoryCenterConfigInfo,
  DemandManagementStatusMCellConfig,
  DemandManagementStatusMCellConfigInfo,
  EmployeeInterest,
  InterestProfileInfo,
  JobStation,
  JobStationInfo,
  JobStationAssignmentDto,
  JobStationAssignmentsSetter,
  MCellCapacity,
  MCellEmployee,
  MCellStatus,
  MCellStatusInfo,
  PersonalProtectiveEquipment,
  PersonalProtectiveEquipmentInfo,
  Shift,
} from './types';
import {
  createDangerousEquipment,
  createEmployeeInterest,
  createInterestProfile,
  createPersonalProtectiveEquipment,
  deleteDangerousEquipment,
  deleteEmployeeInterest,
  deletePersonalProtectiveEquipment,
  getDangerousEquipments,
  getDemandManagementStatusInventoryCenterConfigs,
  getDemandManagementStatusMCellConfigs,
  getEmployeeInterests,
  getInterestProfile,
  getMCellCapacities,
  getMCellEmployeeAccountTypes,
  getMCellEmployeeByEmployeeId,
  getMCellEmployeeCategories,
  getMCellEmployeeRoles,
  getMCellStatuses,
  getPersonalProtectiveEquipments,
  getTopMCells,
  jobStationAddDangerousEquipment,
  jobStationAddPersonalProtectiveEquipment,
  jobStationRemoveDangerousEquipment,
  jobStationRemovePersonalProtectiveEquipment,
  setDemandManagementStatusInventoryCenterConfig,
  setDemandManagementStatusMCellConfig,
  setMCellStatusDemandManagement,
  setMCellStatusDemandManagementBulk,
  setMCellStatusOperations,
  setMCellStatusOperationsBulk,
  updateDangerousEquipment,
  updateEmployeeInterest,
  updateInterestProfile,
  updateMCellCapacity,
  updatePersonalProtectiveEquipment,
  getJobStations,
  getJobStationsWithResolvedDocumentation,
  createJobStation,
  updateJobStation,
  deleteJobStation,
  getCurrentJobStationAssignments,
  getMCellEmployeeById,
  getMCellEmployeeByBadgeId,
  setJobStationAssignments,
  getHomeCellNotificationEnabledInventoryCenters,
  setHomeCellNotificationEnabledInventoryCenters,
} from './api';
import { ManufacturingLocation } from '../manufacturingLocation/types';
import { useAlert } from '../common/AlertContext';
import { QueryKeys } from '../../api/queryKeys';
import { REFERENCE_DATA_STALE_TIME } from '../../queryClient';
import { useInvalidatingMutation } from '../../api/useInvalidatingMutation';
import { EMPTY_GUID } from '../../utils/string-utils';

/**
 * `useJobStationsQuery` is a custom hook that uses React Query to fetch job stations for a manufacturing location.
 *
 * @param {ManufacturingLocation} manufacturingLocation - The manufacturing location to fetch job stations for.
 * @param {boolean} [enabled=true] - Whether the query should be enabled.
 * @returns {UseQueryResult<JobStation[]>} The result object from `useQuery` which includes the data and status of the query.
 */
export const useJobStationsQuery = (
  manufacturingLocation: ManufacturingLocation,
  enabled: boolean = true,
): UseQueryResult<JobStation[]> => {
  return useQuery({
    queryKey: [QueryKeys.JOB_STATIONS, manufacturingLocation],
    queryFn: () => getJobStations(manufacturingLocation),
    meta: { errorMessageKey: 'errorFetchingJobStations' },
    // Guard on the Inventory Center, not the MCell: this list supports the IC-level view
    // (mCellId === EMPTY_GUID means "all job stations in the IC"), so guarding on mCellId
    // would disable exactly that case. We only need a valid IC selected to fetch.
    enabled: enabled && manufacturingLocation.inventoryCenterId !== EMPTY_GUID,
  });
};

/**
 * `useJobStationsWithResolvedDocumentationQuery` fetches job stations with resolved docs links.
 * This uses a distinct query key from `useJobStationsQuery` to avoid cache collisions
 * between raw override-only values and resolved display values.
 */
export const useJobStationsWithResolvedDocumentationQuery = (
  manufacturingLocation: ManufacturingLocation,
  enabled: boolean = true,
): UseQueryResult<JobStation[]> => {
  return useQuery({
    queryKey: [
      QueryKeys.JOB_STATIONS,
      'resolvedDocumentation',
      manufacturingLocation,
    ],
    queryFn: () =>
      getJobStationsWithResolvedDocumentation(manufacturingLocation),
    enabled: enabled && manufacturingLocation.mCellId !== EMPTY_GUID,
  });
};

/**
 * `useCurrentJobStationAssignmentsQuery` is a custom hook that uses React Query to fetch current job station assignments.
 *
 * @param {Object} params - The parameters for fetching assignments.
 * @param {string} params.mCellId - The MCell ID to fetch assignments for.
 * @param {string} params.shift - The shift to fetch assignments for.
 * @param {boolean} [enabled=true] - Whether the query should be enabled.
 * @returns {UseQueryResult<JobStationAssignmentDto[]>} The result object from `useQuery`.
 */
export const useCurrentJobStationAssignmentsQuery = (
  params: { mCellId: string; shift: Shift },
  enabled: boolean = true,
): UseQueryResult<JobStationAssignmentDto[]> => {
  return useQuery({
    queryKey: [QueryKeys.JOB_STATION_ASSIGNMENTS, params],
    queryFn: () => getCurrentJobStationAssignments(params),
    enabled: enabled && params.mCellId !== EMPTY_GUID,
    meta: { errorMessageKey: 'errorFetchingCurrentJobStationAssignments' },
  });
};

/** True while any job station assignments are being fetched or saved. */
export const useIsJobStationAssignmentsLoading = () => {
  const queryKey = [QueryKeys.JOB_STATION_ASSIGNMENTS];
  const fetching = useIsFetching({ queryKey });
  const mutating = useIsMutating({ mutationKey: queryKey });
  return fetching + mutating > 0;
};

/**
 * `useCurrentMCellEmployeeQuery` is a custom hook that uses React Query to fetch an MCell employee by ID.
 *
 * @param {string} employeeId - The ID of the employee to fetch.
 * @param {boolean} [enabled=true] - Whether the query should be enabled.
 * @returns {UseQueryResult<MCellEmployee>} The result object from `useQuery`.
 */
export const useCurrentMCellEmployeeQuery = (
  employeeId: string,
  enabled: boolean = true,
): UseQueryResult<MCellEmployee> => {
  return useQuery({
    queryKey: [QueryKeys.CURRENT_MCELL_EMPLOYEE, employeeId],
    queryFn: () => getMCellEmployeeById(employeeId),
    enabled: enabled && !!employeeId,
  });
};

/**
 * `useSetJobStationAssignmentsMutation` is a custom hook that uses React Query to set job station assignments.
 *
 * @param {UseMutationOptions} [options] - Optional mutation options.
 * @returns The result object from `useMutation`.
 */
export const useSetJobStationAssignmentsMutation = (
  options?: UseMutationOptions<
    JobStationAssignmentDto[],
    Error,
    JobStationAssignmentsSetter
  >,
) => {
  const queryClient = useQueryClient();
  const { setAlert } = useAlert();

  return useMutation({
    mutationKey: [QueryKeys.JOB_STATION_ASSIGNMENTS],
    mutationFn: setJobStationAssignments,
    onSuccess: (data, variables) => {
      // Invalidate and refetch job station assignments for this MCell and shift
      queryClient.setQueryData(
        [
          QueryKeys.JOB_STATION_ASSIGNMENTS,
          { mCellId: variables.mCellId, shift: variables.shift },
        ],
        data,
      );

      // Also invalidate broader queries that might be affected
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.JOB_STATION_ASSIGNMENTS],
        exact: false,
      });
    },
    onError: (error) => {
      setAlert({
        stringKey: 'errorSettingJobStationAssignments',
        props: { error: error.message },
      });
    },
    ...options,
  });
};

/**
 * `useCreateJobStationMutation` creates a job station and invalidates job-station list queries.
 */
export const useCreateJobStationMutation = (
  options?: UseMutationOptions<JobStation, Error, JobStationInfo>,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createJobStation,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.JOB_STATIONS],
        exact: false,
      });
    },
    ...options,
  });
};

/**
 * `useUpdateJobStationMutation` updates a job station and invalidates job-station list queries.
 */
export const useUpdateJobStationMutation = (
  options?: UseMutationOptions<JobStation, Error, JobStationInfo>,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateJobStation,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.JOB_STATIONS],
        exact: false,
      });
    },
    ...options,
  });
};

/**
 * `useDeleteJobStationMutation` deletes a job station and invalidates job-station list queries.
 */
export const useDeleteJobStationMutation = (
  options?: UseMutationOptions<string, Error, string>,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteJobStation,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.JOB_STATIONS],
        exact: false,
      });
    },
    ...options,
  });
};

/**
 * `useMCellEmployeeByBadgeIdMutation` is a custom hook for triggering badge ID lookups.
 * This is useful when you want to manually trigger a badge lookup (e.g., on scan).
 *
 * @param {UseMutationOptions} [options] - Optional mutation options.
 * @returns The result object from `useMutation`.
 */
export const useMCellEmployeeByBadgeIdMutation = (
  options?: UseMutationOptions<MCellEmployee, Error, string>,
) => {
  const queryClient = useQueryClient();
  const { setAlert } = useAlert();

  return useMutation({
    mutationFn: getMCellEmployeeByBadgeId,
    onSuccess: (data, badgeId) => {
      // Cache the result for future queries
      queryClient.setQueryData(
        [QueryKeys.MCELL_EMPLOYEE_BY_BADGE_ID, badgeId],
        data,
      );
      queryClient.setQueryData(
        [QueryKeys.CURRENT_MCELL_EMPLOYEE, data.id],
        data,
      );
    },
    onError: (error, badgeId) => {
      setAlert({
        stringKey: 'employeeNotFoundByBadgeId',
        props: { badgeId: badgeId },
      });
    },
    ...options,
  });
};

/**
 * Custom hook to get the list of inventory center codes that have home cell notifications enabled
 */
export const useHomeCellNotificationEnabledInventoryCentersQuery =
  (): UseQueryResult<string[]> => {
    return useQuery({
      queryKey: [QueryKeys.HOME_CELL_NOTIFICATION_ENABLED_ICS],
      queryFn: getHomeCellNotificationEnabledInventoryCenters,
    });
  };

/**
 * Sets the inventory center codes that have home cell notifications enabled.
 * Stays pending until the enabled list is refetched.
 */
export const useSetHomeCellNotificationEnabledInventoryCentersMutation = () =>
  useInvalidatingMutation(
    [QueryKeys.HOME_CELL_NOTIFICATION_ENABLED_ICS],
    setHomeCellNotificationEnabledInventoryCenters,
    'errorUpdatingSetting',
  );

/** Merges `changes` into the list items matching `isItem`. */
const mergeWhere = <T>(
  list: T[],
  isItem: (item: T) => boolean,
  changes: Partial<T>,
) => list.map((item) => (isItem(item) ? { ...item, ...changes } : item));

/** Fetches all Employee Interests (hobbies). */
export const useEmployeeInterestsQuery = () =>
  useQuery({
    queryKey: [QueryKeys.EMPLOYEE_INTERESTS],
    queryFn: getEmployeeInterests,
    meta: { errorMessageKey: 'errorFetchingEmployeeInterests' },
  });

/** Creates an Employee Interest. */
export const useCreateEmployeeInterestMutation = () =>
  useInvalidatingMutation(
    [QueryKeys.EMPLOYEE_INTERESTS],
    createEmployeeInterest,
    'errorCreatingHobby',
  );

/** Updates an Employee Interest optimistically. */
export const useUpdateEmployeeInterestMutation = () =>
  useInvalidatingMutation(
    [QueryKeys.EMPLOYEE_INTERESTS],
    updateEmployeeInterest,
    'errorUpdatingHobby',
    (list: EmployeeInterest[], { id, employeeInterestInfo }) =>
      mergeWhere(list, (e) => e.id === id, employeeInterestInfo),
  );

/** Deletes an Employee Interest optimistically. */
export const useDeleteEmployeeInterestMutation = () =>
  useInvalidatingMutation(
    [QueryKeys.EMPLOYEE_INTERESTS],
    deleteEmployeeInterest,
    'errorDeletingHobby',
    (list: EmployeeInterest[], id: string) => list.filter((e) => e.id !== id),
  );

/** Fetches an MCell Employee's Interest Profile. Disabled without an employee. */
export const useInterestProfileQuery = (mCellEmployeeId?: string) =>
  useQuery({
    queryKey: [QueryKeys.INTEREST_PROFILE, mCellEmployeeId],
    queryFn: mCellEmployeeId
      ? () => getInterestProfile(mCellEmployeeId)
      : skipToken,
    meta: { errorMessageKey: 'errorFetchingInterestProfile' },
  });

/** Creates an Interest Profile and caches it for its employee. */
export const useCreateInterestProfileMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createInterestProfile,
    onSuccess: (profile, info: InterestProfileInfo) =>
      queryClient.setQueryData(
        [QueryKeys.INTEREST_PROFILE, info.mCellEmployeeId],
        profile,
      ),
    meta: { errorMessageKey: 'errorCreatingInterestProfile' },
  });
};

/** Updates an Interest Profile and caches the result for its employee. */
export const useUpdateInterestProfileMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateInterestProfile,
    onSuccess: (profile, { interestProfileInfo }) =>
      queryClient.setQueryData(
        [QueryKeys.INTEREST_PROFILE, interestProfileInfo.mCellEmployeeId],
        profile,
      ),
    meta: { errorMessageKey: 'errorUpdatingInterestProfile' },
  });
};

/** Fetches an MCell Employee's most worked MCells. Disabled without an employee. */
export const useTopMCellsQuery = (
  mCellEmployeeId: string | undefined,
  topMCellCount: number,
) =>
  useQuery({
    queryKey: [QueryKeys.TOP_MCELLS, mCellEmployeeId, topMCellCount],
    queryFn: mCellEmployeeId
      ? () => getTopMCells({ mCellEmployeeId, topMCellCount })
      : skipToken,
    meta: { errorMessageKey: 'errorFetchingTopWorkCells' },
  });

/** Looks up an MCell Employee by Employee ID (e.g. on manual login). */
export const useMCellEmployeeByEmployeeIdMutation = () =>
  useMutation({
    mutationFn: getMCellEmployeeByEmployeeId,
    meta: { errorMessageKey: 'errorFetchingWorkCellEmployee' },
  });

/** Fetches MCell Capacities (crewing thresholds) for an Inventory Center. */
export const useMCellCapacitiesQuery = (inventoryCenterId: string) =>
  useQuery({
    queryKey: [QueryKeys.MCELL_CAPACITIES, inventoryCenterId],
    queryFn: () => getMCellCapacities(inventoryCenterId),
    enabled: !!inventoryCenterId,
    meta: { errorMessageKey: 'errorFetchingWorkCellCapacities' },
  });

/** Updates an MCell Capacity optimistically. */
export const useUpdateMCellCapacityMutation = () =>
  useInvalidatingMutation(
    [QueryKeys.MCELL_CAPACITIES],
    ({ id, mCell, ...info }: MCellCapacity) => updateMCellCapacity(id, info),
    'errorUpdatingWorkCellCapacity',
    (list: MCellCapacity[], capacity) =>
      mergeWhere(list, (c) => c.id === capacity.id, capacity),
  );

/** Fetches MCell Statuses for a Manufacturing Location. */
export const useMCellStatusesQuery = (
  manufacturingLocation: ManufacturingLocation,
) =>
  useQuery({
    queryKey: [QueryKeys.MCELL_STATUSES, manufacturingLocation],
    queryFn: () => getMCellStatuses(manufacturingLocation),
    meta: { errorMessageKey: 'errorFetchingWorkCellStatuses' },
  });

type MCellStatusField = 'operationsStatus' | 'demandManagementStatus';

/** Optimistically sets `field` on the cached MCell Statuses named in `infos`. */
const patchMCellStatuses =
  (field: MCellStatusField) =>
  (list: MCellStatus[], infos: MCellStatusInfo[]) =>
    list.map((s) => {
      const info = infos.find((i) => i.id === s.id);
      return info ? { ...s, [field]: info.status } : s;
    });

/** Sets one MCell's Operations or Demand Management status optimistically. */
export const useSetMCellStatusMutation = (field: MCellStatusField) => {
  const operations = field === 'operationsStatus';
  return useInvalidatingMutation(
    [QueryKeys.MCELL_STATUSES],
    operations ? setMCellStatusOperations : setMCellStatusDemandManagement,
    operations
      ? 'errorSettingWorkCellStatusOperations'
      : 'errorSettingWorkCellStatusDemandManagement',
    (list: MCellStatus[], info: MCellStatusInfo) =>
      patchMCellStatuses(field)(list, [info]),
  );
};

/** Sets several MCells' Operations or Demand Management statuses optimistically. */
export const useSetMCellStatusBulkMutation = (field: MCellStatusField) => {
  const operations = field === 'operationsStatus';
  return useInvalidatingMutation(
    [QueryKeys.MCELL_STATUSES],
    operations
      ? setMCellStatusOperationsBulk
      : setMCellStatusDemandManagementBulk,
    operations
      ? 'errorSettingWorkCellStatusOperationsBulk'
      : 'errorSettingWorkCellStatusDemandManagementBulk',
    patchMCellStatuses(field),
  );
};

/** Fetches MCell Employee Account Types. */
export const useMCellEmployeeAccountTypesQuery = () =>
  useQuery({
    queryKey: [QueryKeys.MCELL_EMPLOYEE_ACCOUNT_TYPES],
    queryFn: getMCellEmployeeAccountTypes,
    meta: { errorMessageKey: 'errorFetchingAccountTypes' },
  });

/** Fetches MCell Employee Roles. */
export const useMCellEmployeeRolesQuery = () =>
  useQuery({
    queryKey: [QueryKeys.MCELL_EMPLOYEE_ROLES],
    queryFn: getMCellEmployeeRoles,
    staleTime: REFERENCE_DATA_STALE_TIME,
    meta: { errorMessageKey: 'errorFetchingRoles' },
  });

/** Fetches MCell Employee Categories. */
export const useMCellEmployeeCategoriesQuery = () =>
  useQuery({
    queryKey: [QueryKeys.MCELL_EMPLOYEE_CATEGORIES],
    queryFn: getMCellEmployeeCategories,
    meta: { errorMessageKey: 'errorFetchingCategories' },
  });

/** Fetches Demand Management Status automation rules for all Inventory Centers. */
export const useDemandManagementStatusInventoryCenterConfigsQuery = () =>
  useQuery({
    queryKey: [QueryKeys.DEMAND_MANAGEMENT_STATUS_INVENTORY_CENTER_CONFIGS],
    queryFn: getDemandManagementStatusInventoryCenterConfigs,
    meta: { errorMessageKey: 'errorFetchingDemandManagementConfigurations' },
  });

/** Sets an Inventory Center's Demand Management Status automation rules optimistically. */
export const useSetDemandManagementStatusInventoryCenterConfigMutation = () =>
  useInvalidatingMutation(
    [QueryKeys.DEMAND_MANAGEMENT_STATUS_INVENTORY_CENTER_CONFIGS],
    setDemandManagementStatusInventoryCenterConfig,
    'errorSettingDemandManagementConfiguration',
    (
      list: DemandManagementStatusInventoryCenterConfig[],
      info: DemandManagementStatusInventoryCenterConfigInfo,
    ) =>
      mergeWhere(
        list,
        (c) => c.inventoryCenterId === info.inventoryCenterId,
        info,
      ),
  );

/** Fetches Demand Management Status automation modes for all MCells. */
export const useDemandManagementStatusMCellConfigsQuery = () =>
  useQuery({
    queryKey: [QueryKeys.DEMAND_MANAGEMENT_STATUS_MCELL_CONFIGS],
    queryFn: getDemandManagementStatusMCellConfigs,
    meta: { errorMessageKey: 'errorFetchingDemandManagementConfigurations' },
  });

/** Sets an MCell's Demand Management Status automation mode optimistically. */
export const useSetDemandManagementStatusMCellConfigMutation = () =>
  useInvalidatingMutation(
    [QueryKeys.DEMAND_MANAGEMENT_STATUS_MCELL_CONFIGS],
    setDemandManagementStatusMCellConfig,
    'errorSettingDemandManagementConfiguration',
    (
      list: DemandManagementStatusMCellConfig[],
      info: DemandManagementStatusMCellConfigInfo,
    ) => mergeWhere(list, (c) => c.mCellId === info.mCellId, info),
  );

/** Fetches Personal Protective Equipment for an Inventory Center. */
export const usePersonalProtectiveEquipmentsQuery = (
  inventoryCenterId: string,
) =>
  useQuery({
    queryKey: [QueryKeys.PERSONAL_PROTECTIVE_EQUIPMENTS, inventoryCenterId],
    queryFn: () => getPersonalProtectiveEquipments(inventoryCenterId),
    staleTime: REFERENCE_DATA_STALE_TIME,
    meta: { errorMessageKey: 'errorFetchingPersonalProtectiveEquipments' },
  });

/** Creates Personal Protective Equipment. */
export const useCreatePersonalProtectiveEquipmentMutation = () =>
  useInvalidatingMutation(
    [QueryKeys.PERSONAL_PROTECTIVE_EQUIPMENTS],
    createPersonalProtectiveEquipment,
    'errorCreatingPersonalProtectiveEquipment',
  );

/** Updates Personal Protective Equipment optimistically. */
export const useUpdatePersonalProtectiveEquipmentMutation = () =>
  useInvalidatingMutation(
    [QueryKeys.PERSONAL_PROTECTIVE_EQUIPMENTS],
    updatePersonalProtectiveEquipment,
    'errorUpdatingPersonalProtectiveEquipment',
    (
      list: PersonalProtectiveEquipment[],
      info: PersonalProtectiveEquipmentInfo,
    ) => mergeWhere(list, (e) => e.id === info.id, info),
  );

/** Deletes Personal Protective Equipment optimistically. */
export const useDeletePersonalProtectiveEquipmentMutation = () =>
  useInvalidatingMutation(
    [QueryKeys.PERSONAL_PROTECTIVE_EQUIPMENTS],
    deletePersonalProtectiveEquipment,
    'errorDeletingPersonalProtectiveEquipment',
    (list: PersonalProtectiveEquipment[], id: string) =>
      list.filter((e) => e.id !== id),
  );

/** Fetches Dangerous Equipment for an Inventory Center. */
export const useDangerousEquipmentsQuery = (inventoryCenterId: string) =>
  useQuery({
    queryKey: [QueryKeys.DANGEROUS_EQUIPMENTS, inventoryCenterId],
    queryFn: () => getDangerousEquipments(inventoryCenterId),
    staleTime: REFERENCE_DATA_STALE_TIME,
    meta: { errorMessageKey: 'errorFetchingDangerousEquipments' },
  });

/** Creates Dangerous Equipment. */
export const useCreateDangerousEquipmentMutation = () =>
  useInvalidatingMutation(
    [QueryKeys.DANGEROUS_EQUIPMENTS],
    createDangerousEquipment,
    'errorCreatingDangerousEquipment',
  );

/** Updates Dangerous Equipment optimistically. */
export const useUpdateDangerousEquipmentMutation = () =>
  useInvalidatingMutation(
    [QueryKeys.DANGEROUS_EQUIPMENTS],
    updateDangerousEquipment,
    'errorUpdatingDangerousEquipment',
    (list: DangerousEquipment[], info: DangerousEquipmentInfo) =>
      mergeWhere(list, (e) => e.id === info.id, info),
  );

/** Deletes Dangerous Equipment optimistically. */
export const useDeleteDangerousEquipmentMutation = () =>
  useInvalidatingMutation(
    [QueryKeys.DANGEROUS_EQUIPMENTS],
    deleteDangerousEquipment,
    'errorDeletingDangerousEquipment',
    (list: DangerousEquipment[], id: string) => list.filter((e) => e.id !== id),
  );

/** Adds Personal Protective Equipment to a Job Station. */
export const useJobStationAddPersonalProtectiveEquipmentMutation = () =>
  useInvalidatingMutation(
    [QueryKeys.JOB_STATIONS],
    jobStationAddPersonalProtectiveEquipment,
    'errorAddingJobStationPersonalProtectiveEquipment',
  );

/** Removes Personal Protective Equipment from a Job Station. */
export const useJobStationRemovePersonalProtectiveEquipmentMutation = () =>
  useInvalidatingMutation(
    [QueryKeys.JOB_STATIONS],
    jobStationRemovePersonalProtectiveEquipment,
    'errorRemovingJobStationPersonalProtectiveEquipment',
  );

/** Adds Dangerous Equipment to a Job Station. */
export const useJobStationAddDangerousEquipmentMutation = () =>
  useInvalidatingMutation(
    [QueryKeys.JOB_STATIONS],
    jobStationAddDangerousEquipment,
    'errorAddingJobStationDangerousEquipment',
  );

/** Removes Dangerous Equipment from a Job Station. */
export const useJobStationRemoveDangerousEquipmentMutation = () =>
  useInvalidatingMutation(
    [QueryKeys.JOB_STATIONS],
    jobStationRemoveDangerousEquipment,
    'errorRemovingJobStationDangerousEquipment',
  );
